/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Upload, Trash2, Building2, Receipt, Palette, ShieldCheck, Smartphone, Link2, Copy, Check , Euro } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { impostazioniAPI } from '../api/impostazioni';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#8B5CF6';
const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || '';

// Le tre dashboard. Titolo e icona qui sono facoltativi: servono a distinguerle
// quando finiscono sull'home screen dello stesso telefono, dove altrimenti sarebbero
// tre icone identiche. Lasciati vuoti vale l'identita' generale.
const APP = [
  { area: 'cliente', nome: 'App cliente',  colore: '#3B82F6' },
  { area: 'tecnico', nome: 'App tecnico',  colore: '#10B981' },
  { area: 'admin',   nome: 'Pannello admin', colore: '#8B5CF6' },
];

// Indirizzi mostrati in sola lettura. L'API sta in fondo perche' interessa a chi
// amministra, non a chi deve mandare un link a un cliente.
const LINK = [
  { chiave: 'cliente', etichetta: 'Area cliente',   colore: '#3B82F6' },
  { chiave: 'tecnico', etichetta: 'Area tecnico',   colore: '#10B981' },
  { chiave: 'admin',   etichetta: 'Pannello admin', colore: '#8B5CF6' },
  { chiave: 'api',     etichetta: 'API',            colore: '#6B7280' },
];

// I campi sono raggruppati per quello che servono a fare, non per come stanno nel
// database: chi compila deve capire perché gli si chiede un dato.
const GRUPPI = [
  {
    titolo: 'Identità dell\'applicazione',
    icona: Palette,
    nota: 'Compaiono nell\'intestazione e nella schermata di accesso di tutte e tre le app.',
    campi: [
      { chiave: 'app_nome', label: 'Nome dell\'applicazione *', max: 60 },
      { chiave: 'app_descrizione', label: 'Descrizione', max: 200 },
      { chiave: 'prefisso_codice', label: 'Prefisso dei codici intervento', max: 8,
        aiuto: 'Da 2 a 8 lettere o cifre. Vale solo per i nuovi interventi: i codici già assegnati non cambiano.' },
    ],
  },
  {
    titolo: 'Anagrafica azienda',
    icona: Building2,
    nota: 'Compare nell\'informativa privacy come titolare del trattamento: per legge deve essere corretta e aggiornata.',
    campi: [
      { chiave: 'azienda_denominazione', label: 'Denominazione / ragione sociale', max: 255 },
      { chiave: 'azienda_partita_iva', label: 'Partita IVA', max: 13 },
      { chiave: 'azienda_codice_fiscale', label: 'Codice fiscale', max: 16 },
      { chiave: 'azienda_indirizzo', label: 'Indirizzo sede legale', max: 255 },
      { chiave: 'azienda_cap', label: 'CAP', max: 5, stretto: true },
      { chiave: 'azienda_comune', label: 'Comune', max: 100 },
      { chiave: 'azienda_provincia', label: 'Provincia', max: 2, stretto: true, aiuto: 'Sigla, es. BO' },
      { chiave: 'azienda_nazione', label: 'Nazione', max: 2, stretto: true, aiuto: 'Sigla, es. IT' },
      { chiave: 'azienda_telefono', label: 'Telefono', max: 30 },
      { chiave: 'azienda_email', label: 'Email', max: 255 },
      { chiave: 'azienda_pec', label: 'PEC', max: 255 },
      { chiave: 'azienda_attivita', label: 'Attività svolta', max: 100,
        aiuto: 'Come si chiama il servizio erogato, es. "assistenza tecnica", "impianti elettrici". Compare nelle finalità dell\'informativa.' },
    ],
  },
  {
    titolo: 'Informativa privacy',
    icona: ShieldCheck,
    nota: 'Il testo dell\'informativa è fisso nella parte giuridica (basi, diritti, riferimenti al GDPR); qui si regolano le voci che dipendono dall\'azienda.',
    campi: [
      { chiave: 'privacy_conservazione_anni', label: 'Anni di conservazione dei documenti fiscali', max: 2, stretto: true,
        aiuto: 'In Italia sono 10' },
      { chiave: 'privacy_dpo', label: 'Contatto del DPO', max: 255,
        aiuto: 'Solo se ne è stato nominato uno: se lo lasci vuoto, il paragrafo non compare' },
      { chiave: 'privacy_aggiornata_il', label: 'Informativa aggiornata al', max: 10, stretto: true,
        aiuto: 'Formato AAAA-MM-GG' },
      { chiave: "privacy_trasferimenti", label: "Trasferimento dei dati fuori dall’UE", max: 500, lungo: true,
        aiuto: "Obbligatorio per l’art. 13.1.f GDPR. VERIFICA che corrisponda alla tua infrastruttura: se un fornitore (hosting, email, backup) tratta i dati fuori dall’UE, va dichiarato qui." },
    ],
  },
  {
    titolo: 'Prezzi e tariffe',
    icona: Euro,
    nota: 'La tariffa e\' il numero da cui si calcola la manodopera: ore lavorate per tariffa. Va scritta IVA esclusa.',
    campi: [
      { chiave: 'tariffa_oraria_standard', label: 'Tariffa oraria standard (euro, IVA esclusa)', max: 10, stretto: true,
        aiuto: 'Vale da adesso in avanti. Gli interventi gia\' chiusi con un importo scritto a mano non cambiano.' },
    ],
  },
  {
    titolo: 'Dati per la fatturazione',
    icona: Receipt,
    nota: 'Non vengono mostrati ai clienti. Servono alla fattura elettronica: il codice SDI è quello con cui l\'azienda RICEVE le fatture dai fornitori.',
    campi: [
      { chiave: 'azienda_codice_sdi', label: 'Codice destinatario (SDI)', max: 7, aiuto: '7 caratteri per i privati, 6 per la pubblica amministrazione' },
      { chiave: 'azienda_regime_fiscale', label: 'Regime fiscale', max: 4, stretto: true, aiuto: 'Formato RFxx, es. RF01 (ordinario)' },
      { chiave: 'azienda_rea', label: 'Numero REA', max: 20 },
      { chiave: 'azienda_iban', label: 'IBAN', max: 34 },
    ],
  },
];

export default function Impostazioni() {
  const qc = useQueryClient();
  const { isMobile } = useViewport();
  const fileInput = useRef(null);
  const faviconInput = useRef(null);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['impostazioni-tutte'],
    queryFn: () => impostazioniAPI.leggiTutte(),
  });

  // Non cambiano mai a caldo: arrivano dal .env del server.
  const { data: link } = useQuery({
    queryKey: ['impostazioni-link'],
    queryFn: () => impostazioniAPI.leggiLink(),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (data && !form) {
      const iniziale = {};
      for (const g of GRUPPI) for (const c of g.campi) iniziale[c.chiave] = data[c.chiave] || '';
      for (const a of APP) iniziale[`app_nome_${a.area}`] = data[`app_nome_${a.area}`] || '';
      setForm(iniziale);
    }
  }, [data, form]);

  const dopoSalvataggio = (messaggio) => {
    setErr('');
    setOk(messaggio);
    qc.invalidateQueries({ queryKey: ['impostazioni-tutte'] });
    qc.invalidateQueries({ queryKey: ['impostazioni'] });   // l'intestazione si aggiorna da sola
  };

  const salvaMut = useMutation({
    mutationFn: () => impostazioniAPI.salva(form),
    onSuccess: () => dopoSalvataggio('Impostazioni salvate.'),
    onError: (e) => { setOk(''); setErr(e.response?.data?.error || 'Errore nel salvataggio'); },
  });

  const logoMut = useMutation({
    mutationFn: (file) => impostazioniAPI.caricaLogo(file),
    onSuccess: () => dopoSalvataggio('Logo aggiornato.'),
    onError: (e) => { setOk(''); setErr(e.response?.data?.error || 'Errore nel caricamento'); },
  });

  const rimuoviLogoMut = useMutation({
    mutationFn: () => impostazioniAPI.rimuoviLogo(),
    onSuccess: () => dopoSalvataggio('Logo rimosso.'),
    onError: (e) => { setOk(''); setErr(e.response?.data?.error || 'Errore'); },
  });

  const faviconMut = useMutation({
    mutationFn: (file) => impostazioniAPI.caricaFavicon(file),
    onSuccess: () => dopoSalvataggio('Icona aggiornata. Ricarica la pagina per vederla nella scheda.'),
    onError: (e) => { setOk(''); setErr(e.response?.data?.error || 'Errore nel caricamento'); },
  });

  const rimuoviFaviconMut = useMutation({
    mutationFn: () => impostazioniAPI.rimuoviFavicon(),
    onSuccess: () => dopoSalvataggio('Icona rimossa: si torna a usare il logo.'),
    onError: (e) => { setOk(''); setErr(e.response?.data?.error || 'Errore'); },
  });

  const iconaAreaMut = useMutation({
    mutationFn: ({ area, file }) => impostazioniAPI.caricaIconaArea(area, file),
    onSuccess: () => dopoSalvataggio("Icona aggiornata. Chi ha già l'app sull'home screen deve reinstallarla per vederla."),
    onError: (e) => { setOk(''); setErr(e.response?.data?.error || 'Errore nel caricamento'); },
  });

  const rimuoviIconaAreaMut = useMutation({
    mutationFn: (area) => impostazioniAPI.rimuoviIconaArea(area),
    onSuccess: () => dopoSalvataggio('Icona rimossa: si torna a usare quella generale.'),
    onError: (e) => { setOk(''); setErr(e.response?.data?.error || 'Errore'); },
  });

  if (isLoading || !form) {
    return <AdminLayout title="Impostazioni"><div style={styles.loading}>Caricamento...</div></AdminLayout>;
  }

  const percorsoImmagine = (v) => (v ? `${UPLOADS_URL}${v.replace('/uploads', '')}` : null);
  const logoUrl = percorsoImmagine(data.app_logo_url);
  const faviconUrl = percorsoImmagine(data.app_favicon_url);

  return (
    <AdminLayout title="Impostazioni">
      {err && <div style={styles.errBox}>{err}</div>}
      {ok && <div style={styles.okBox}>{ok}</div>}

      {/* Logo */}
      <div style={styles.sezione}>
        <h3 style={styles.sezioneTitolo}><Palette size={18} /> Logo</h3>
        <p style={styles.nota}>PNG, JPG, WEBP o SVG, massimo 2 MB. Se non c'è, resta il quadratino colorato.</p>
        <div style={styles.logoRiga}>
          <div style={styles.logoBox}>
            {logoUrl
              ? <img src={logoUrl} alt="Logo" style={styles.logoImg} />
              : <span style={styles.logoVuoto}>nessun logo</span>}
          </div>
          <div style={styles.logoAzioni}>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files[0]; if (f) logoMut.mutate(f); e.target.value = ''; }}
            />
            <button onClick={() => fileInput.current?.click()} disabled={logoMut.isPending} style={styles.btnSecondario}>
              <Upload size={15} /> {logoMut.isPending ? 'Carico...' : 'Carica logo'}
            </button>
            {logoUrl && (
              <button onClick={() => rimuoviLogoMut.mutate()} disabled={rimuoviLogoMut.isPending} style={styles.btnPericolo}>
                <Trash2 size={15} /> Rimuovi
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Icona della scheda */}
      <div style={styles.sezione}>
        <h3 style={styles.sezioneTitolo}><Palette size={18} /> Icona della scheda</h3>
        <p style={styles.nota}>
          L&apos;immagine che compare nella linguetta del browser. ICO, PNG o SVG, massimo 512 KB,
          preferibilmente quadrata. Se non la carichi viene usato il logo.
        </p>
        <div style={styles.logoRiga}>
          <div style={{ ...styles.logoBox, width: '64px', height: '64px' }}>
            {faviconUrl
              ? <img src={faviconUrl} alt="Icona" style={styles.logoImg} />
              : logoUrl
                ? <img src={logoUrl} alt="Logo usato come icona" style={{ ...styles.logoImg, opacity: 0.55 }} />
                : <span style={styles.logoVuoto}>—</span>}
          </div>
          <div style={styles.logoAzioni}>
            <input
              ref={faviconInput}
              type="file"
              accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml,.ico"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files[0]; if (f) faviconMut.mutate(f); e.target.value = ''; }}
            />
            <button onClick={() => faviconInput.current?.click()} disabled={faviconMut.isPending} style={styles.btnSecondario}>
              <Upload size={15} /> {faviconMut.isPending ? 'Carico...' : 'Carica icona'}
            </button>
            {faviconUrl && (
              <button onClick={() => rimuoviFaviconMut.mutate()} disabled={rimuoviFaviconMut.isPending} style={styles.btnPericolo}>
                <Trash2 size={15} /> Rimuovi
              </button>
            )}
          </div>
        </div>
        {!faviconUrl && logoUrl && <p style={styles.nota}>Ora è in uso il logo (mostrato sbiadito qui sopra).</p>}
      </div>

      {/* Titolo e icona delle singole app */}
      <div style={styles.sezione}>
        <h3 style={styles.sezioneTitolo}><Smartphone size={18} /> Le tre app sul telefono</h3>
        <p style={styles.nota}>
          Quando qualcuno aggiunge l&apos;app alla schermata Home del telefono, il nome e
          l&apos;icona arrivano da qui. Serve solo se vuoi distinguerle: <strong>lasciando
          tutto vuoto</strong> ognuna usa il nome e il logo generali, e le tre icone
          risultano uguali. PNG, JPG, WEBP o SVG, massimo 2 MB, meglio quadrata.
        </p>
        {APP.map((a) => (
          <RigaApp
            key={a.area}
            app={a}
            nome={form[`app_nome_${a.area}`]}
            onNome={(v) => setForm({ ...form, [`app_nome_${a.area}`]: v })}
            iconaUrl={percorsoImmagine(data[`app_icona_${a.area}_url`])}
            ripiego={faviconUrl || logoUrl}
            nomeRipiego={form.app_nome || data.app_nome}
            onCarica={(file) => iconaAreaMut.mutate({ area: a.area, file })}
            onRimuovi={() => rimuoviIconaAreaMut.mutate(a.area)}
            occupato={iconaAreaMut.isPending || rimuoviIconaAreaMut.isPending}
          />
        ))}
        <p style={styles.nota}>
          Il nome si salva col pulsante in fondo alla pagina; l&apos;icona si carica subito.
          Chi ha gi&agrave; l&apos;app sull&apos;home screen deve toglierla e rimetterla per
          vedere il cambiamento: il telefono la copia al momento dell&apos;installazione.
        </p>
      </div>

      {/* Indirizzi delle app */}
      <div style={styles.sezione}>
        <h3 style={styles.sezioneTitolo}><Link2 size={18} /> Indirizzi delle app</h3>
        <p style={styles.nota}>
          Da passare a clienti e tecnici. Non si modificano da qui: sono decisi
          all&apos;installazione, insieme al dominio e al certificato, e stanno nella
          configurazione del server. Sono anche le uniche origini da cui l&apos;API
          accetta richieste.
        </p>
        {LINK.map((l) => (
          <RigaLink key={l.chiave} etichetta={l.etichetta} colore={l.colore} url={link && link[l.chiave]} />
        ))}
      </div>

      {GRUPPI.map((g) => {
        const Icona = g.icona;
        return (
          <div key={g.titolo} style={styles.sezione}>
            <h3 style={styles.sezioneTitolo}><Icona size={18} /> {g.titolo}</h3>
            <p style={styles.nota}>{g.nota}</p>
            <div style={{ ...styles.griglia, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
              {g.campi.map((c) => (
                <div key={c.chiave} style={{ ...styles.campo, ...(c.lungo && !isMobile ? { gridColumn: 'span 2' } : {}) }}>
                  <label style={styles.label}>{c.label}</label>
                  {c.lungo ? (
                    <textarea
                      value={form[c.chiave]}
                      maxLength={c.max}
                      onChange={(e) => setForm({ ...form, [c.chiave]: e.target.value })}
                      style={{ ...styles.input, minHeight: '70px', fontFamily: 'inherit' }}
                    />
                  ) : (
                    <input
                      value={form[c.chiave]}
                      maxLength={c.max}
                      onChange={(e) => setForm({ ...form, [c.chiave]: e.target.value })}
                      style={styles.input}
                    />
                  )}
                  {c.aiuto && <span style={styles.aiuto}>{c.aiuto}</span>}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div style={styles.barraSalva}>
        <button onClick={() => salvaMut.mutate()} disabled={salvaMut.isPending} style={styles.btnSalva}>
          <Save size={16} /> {salvaMut.isPending ? 'Salvo...' : 'Salva impostazioni'}
        </button>
      </div>
    </AdminLayout>
  );
}

// Una riga per indirizzo. Se la variabile non e' impostata sul server lo si dice,
// invece di mostrare un vuoto che sembra un guasto.
function RigaLink({ etichetta, colore, url }) {
  const [copiato, setCopiato] = useState(false);

  const copia = () => {
    if (!url || !navigator.clipboard) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiato(true);
      setTimeout(() => setCopiato(false), 1800);
    });
  };

  return (
    <div style={styles.linkRiga}>
      <span style={{ ...styles.linkPunto, backgroundColor: colore }} />
      <span style={styles.linkEtichetta}>{etichetta}</span>
      {url ? (
        <>
          <a href={url} target="_blank" rel="noopener noreferrer" style={styles.linkUrl}>{url}</a>
          <button onClick={copia} style={styles.btnCopia} title="Copia l'indirizzo">
            {copiato ? <Check size={14} /> : <Copy size={14} />} {copiato ? 'Copiato' : 'Copia'}
          </button>
        </>
      ) : (
        <span style={styles.linkMancante}>non impostato nella configurazione del server</span>
      )}
    </div>
  );
}

// Una riga per app: nome, icona e i due pulsanti. Sta qui e non in un file a parte
// perche' vive solo dentro questa pagina.
function RigaApp({ app, nome, onNome, iconaUrl, ripiego, nomeRipiego, onCarica, onRimuovi, occupato }) {
  const input = useRef(null);
  return (
    <div style={styles.appRiga}>
      <div style={{ ...styles.appIcona, borderColor: app.colore }}>
        {iconaUrl
          ? <img src={iconaUrl} alt={app.nome} style={styles.logoImg} />
          : ripiego
            ? <img src={ripiego} alt="Icona generale" style={{ ...styles.logoImg, opacity: 0.45 }} />
            : <span style={styles.logoVuoto}>&mdash;</span>}
      </div>
      <div style={styles.appCorpo}>
        <span style={{ ...styles.appEtichetta, color: app.colore }}>{app.nome}</span>
        <input
          value={nome}
          maxLength={60}
          placeholder={nomeRipiego || 'Nome generale'}
          onChange={(e) => onNome(e.target.value)}
          style={styles.input}
        />
        <span style={styles.aiuto}>
          {nome.trim()
            ? 'Sul telefono si legge “' + nome.trim() + '”.'
            : 'Vuoto: usa il nome generale.'}
        </span>
      </div>
      <div style={styles.logoAzioni}>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files[0]; if (f) onCarica(f); e.target.value = ''; }}
        />
        <button onClick={() => input.current?.click()} disabled={occupato} style={styles.btnSecondario}>
          <Upload size={15} /> Icona
        </button>
        {iconaUrl && (
          <button onClick={onRimuovi} disabled={occupato} style={styles.btnPericolo}>
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  sezione: { backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.25rem' },
  sezioneTitolo: { margin: '0 0 0.4rem', fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.45rem' },
  nota: { margin: '0 0 1rem', fontSize: '0.83rem', color: '#6B7280', lineHeight: 1.45 },
  griglia: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.85rem' },
  campo: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.6rem 0.8rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  aiuto: { fontSize: '0.75rem', color: '#9CA3AF' },
  linkRiga: { display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.55rem 0', borderTop: '1px solid #F3F4F6', flexWrap: 'wrap' },
  linkPunto: { width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0 },
  linkEtichetta: { fontSize: '0.82rem', fontWeight: 600, color: '#374151', minWidth: '120px' },
  linkUrl: { fontSize: '0.86rem', color: '#4B5563', textDecoration: 'none', wordBreak: 'break-all', flex: '1 1 200px' },
  linkMancante: { fontSize: '0.8rem', color: '#9CA3AF', fontStyle: 'italic' },
  btnCopia: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' },
  appRiga: { display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '0.9rem 0', borderTop: '1px solid #F3F4F6', flexWrap: 'wrap' },
  appIcona: { width: '58px', height: '58px', border: '2px solid', borderRadius: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB', overflow: 'hidden', flexShrink: 0 },
  appCorpo: { display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 220px', minWidth: '200px' },
  appEtichetta: { fontSize: '0.82rem', fontWeight: 700 },
  logoRiga: { display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' },
  logoBox: { width: '120px', height: '120px', border: '1px dashed #D1D5DB', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB', overflow: 'hidden' },
  logoImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  logoVuoto: { fontSize: '0.78rem', color: '#9CA3AF' },
  logoAzioni: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap' },
  btnSecondario: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' },
  btnPericolo: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: 'white', color: '#B91C1C', border: '1px solid #FCA5A5', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' },
  barraSalva: { display: 'flex', justifyContent: 'flex-end', marginBottom: '2rem' },
  btnSalva: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.7rem 1.4rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem' },
  errBox: { padding: '0.75rem 1rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.88rem', marginBottom: '1rem' },
  okBox: { padding: '0.75rem 1rem', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '8px', fontSize: '0.88rem', marginBottom: '1rem' },
};
